import { useState, useEffect, useRef } from "react";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  where,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { exportTenantData } from '../services/exportTenant';
import AuditoriaPanel from "./AuditoriaPanel";
import IntegracionesPanel from "./IntegracionesPanel";
const createInvitationFn = httpsCallable(functions, "createInvitation");
const setUserAppRoleFn = httpsCallable(functions, "setUserAppRole");
const setUserObrasFn = httpsCallable(functions, "setUserObras");
const cancelInvitationFn = httpsCallable(functions, "cancelInvitation");
const setUserTenantsFn = httpsCallable(functions, "setUserTenants");
// Buscador reutilizable — mismo componente en las 5 secciones del panel.
// `total` y `resultados` son opcionales: si se pasan, muestra "N de M".
function Buscador({ value, onChange, placeholder, resultados, total }) {
  const filtrando = value.trim().length > 0;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ position: "relative" }}>
        <span
          style={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 14,
            color: "#94a3b8",
            pointerEvents: "none",
          }}
        >
          &#128269;
        </span>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "10px 36px 10px 36px",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            fontSize: 14,
            fontFamily: "inherit",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        {filtrando && (
          <button
            onClick={() => onChange("")}
            title="Limpiar"
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 14,
              color: "#94a3b8",
              padding: 4,
              lineHeight: 1,
            }}
          >
            &#10005;
          </button>
        )}
      </div>
      {filtrando && typeof resultados === "number" && (
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
          {resultados === 0
            ? "Sin resultados"
            : "Mostrando " + resultados + " de " + total}
        </div>
      )}
    </div>
  );
}

// Busca el texto en cualquiera de los campos que se le pasen.
function coincide(q, ...campos) {
  const t = String(q || "").trim().toLowerCase();
  if (!t) return true;
  return campos.some((c) => String(c || "").toLowerCase().includes(t));
}

function LogoUploader({ tenantId, currentUrl, onUploaded, onRemove }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  async function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const maxMB = 2;
    if (file.size > maxMB * 1024 * 1024) {
      setError(`El archivo debe ser menor a ${maxMB}MB`);
      return;
    }
    if (!["image/png", "image/jpeg", "image/svg+xml", "image/webp"].includes(file.type)) {
      setError("Formato no válido. Usá PNG, JPG, SVG o WEBP.");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const path = `tenants/${tenantId}/logo`;
      const fileRef = storageRef(storage, path);
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      onUploaded(url);
    } catch (e) {
      setError("Error al subir: " + e.message);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div style={{ marginTop: 16, marginBottom: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#64748b",
        display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        Logo de la empresa
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        {currentUrl ? (
          <div style={{ position: "relative" }}>
            <img src={currentUrl} alt="Logo"
              style={{ height: 40, maxWidth: 160, objectFit: "contain",
                border: "1px solid #e2e8f0", borderRadius: 8, padding: "4px 8px", background: "#f8fafc" }} />
            <button onClick={onRemove}
              style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18,
                borderRadius: "50%", background: "#ef4444", border: "none", color: "white",
                fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 700, lineHeight: 1 }}>
              ✕
            </button>
          </div>
        ) : (
          <div style={{ width: 80, height: 40, border: "1.5px dashed #cbd5e1", borderRadius: 8,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, color: "#94a3b8" }}>
            Sin logo
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <button onClick={() => inputRef.current?.click()} disabled={uploading}
            style={{ padding: "6px 14px", background: uploading ? "#e2e8f0" : "#f1f5f9",
              border: "1px solid #cbd5e1", borderRadius: 6, cursor: uploading ? "default" : "pointer",
              fontSize: 12, fontWeight: 600, color: "#475569" }}>
            {uploading ? "Subiendo…" : currentUrl ? "Cambiar logo" : "Subir logo"}
          </button>
          <span style={{ fontSize: 10, color: "#94a3b8" }}>PNG, JPG, SVG · máx 2MB</span>
        </div>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
          onChange={handleFile} style={{ display: "none" }} />
      </div>
      {error && <p style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>{error}</p>}
    </div>
  );
}

export default function AdminPage({ user, userProfile }) {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [apps, setApps] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  const [qTenants, setQTenants] = useState("");
  const [qUsers, setQUsers] = useState("");
  const [qInvitations, setQInvitations] = useState("");
  const [qApps, setQApps] = useState("");
  const [qObras, setQObras] = useState("");
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [copiedToken, setCopiedToken] = useState("");
  const [userAppRoles, setUserAppRoles] = useState([]);
  const [tenantApps, setTenantApps] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserRole, setEditUserRole] = useState("");
  const [editApps, setEditApps] = useState({});
  const [editObrasModo, setEditObrasModo] = useState("todas");
  const [editObrasSel, setEditObrasSel] = useState([]);
  const [editTenantIds, setEditTenantIds] = useState([]);
  // Empresa sobre la que se están editando permisos y obras (multi-empresa).
  const [editTenantCtx, setEditTenantCtx] = useState(null);
  const [savingApps, setSavingApps] = useState(false);
  const [showTenantForm, setShowTenantForm] = useState(false);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [editingTenant, setEditingTenant] = useState(null);
  const [editTenantData, setEditTenantData] = useState({});
  const [editTenantApps, setEditTenantApps] = useState({});
  const [savingTenant, setSavingTenant] = useState(false);
  const [obras, setObras] = useState([]);
  const [obrasLoading, setObrasLoading] = useState(false);
  const [showObraForm, setShowObraForm] = useState(false);
  const [savingObra, setSavingObra] = useState(false);
  const [editingObra, setEditingObra] = useState(null);
  const [editObraData, setEditObraData] = useState({});
  const [savingEditObra, setSavingEditObra] = useState(false);
  const [appObras, setAppObras] = useState({});
  const [expandedObra, setExpandedObra] = useState(null);
  const [obraData, setObraData] = useState({
    nombre: "",
    direccion: "",
    comitente: "",
    tipo_obra: "VIVIENDAS_UNIFAMILIARES",
    tenant_id: "",
  });
  const [tenantData, setTenantData] = useState({
    id: "",
    name: "",
    plan: "basic",
    primaryColor: "#2563eb",
    secondaryColor: "#f1f5f9",
    logoText: "",
    logoSubtext: "",
    adminEmail: "",
  });

  const isSuperadmin = userProfile?.role_platform === "superadmin";

  const [inviteData, setInviteData] = useState({
    email: "",
    role_platform: "user",
    tenant_id: userProfile?.tenant_id || "",
    apps: {},
  });

  useEffect(() => {
    if (userProfile?.tenant_id && !inviteData.tenant_id) {
      setInviteData((prev) => ({ ...prev, tenant_id: userProfile.tenant_id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.tenant_id]);

  useEffect(() => {
    loadData(); // eslint-disable-next-line
  }, []);
  async function loadData() {
    try {
      const isSuperadminCheck = userProfile?.role_platform === "superadmin";
      const myTenantCheck = userProfile?.tenant_id;

      const [
        usersSnap,
        appsSnap,
        tenantsSnap,
        invSnap,
        rolesSnap,
        tenantAppsSnap,
      ] = await Promise.all([
        isSuperadminCheck
          ? getDocs(collection(db, "users"))
          : getDocs(
              query(
                collection(db, "users"),
                where("tenant_id", "==", myTenantCheck),
              ),
            ),
        getDocs(collection(db, "apps")),
        getDocs(collection(db, "tenants")),
        isSuperadminCheck
          ? getDocs(collection(db, "invitations"))
          : getDocs(
              query(
                collection(db, "invitations"),
                where("tenant_id", "==", myTenantCheck),
              ),
            ),
        isSuperadminCheck
          ? getDocs(collection(db, "user_app_roles"))
          : getDocs(
              query(
                collection(db, "user_app_roles"),
                where("tenant_id", "==", myTenantCheck),
              ),
            ),
        getDocs(collection(db, "tenant_apps")),
      ]);

      const isSuperadmin = userProfile?.role_platform === "superadmin";
      const myTenant = userProfile?.tenant_id;

      // Superadmin ve todo, tenant_admin ve solo su empresa
      const allUsers = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const allInvitations = invSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      const allRoles = rolesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setUsers(
        isSuperadmin
          ? allUsers
          : allUsers.filter(
              (u) =>
                u.tenant_id === myTenant && u.role_platform !== "superadmin",
            ),
      );
      setApps(
        appsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0)),
      );
      setTenants(
        isSuperadmin
          ? tenantsSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
          : tenantsSnap.docs
              .map((d) => ({ id: d.id, ...d.data() }))
              .filter((t) => t.id === myTenant),
      );
      setInvitations(
        isSuperadmin
          ? allInvitations
          : allInvitations.filter((i) => i.tenant_id === myTenant),
      );
      setUserAppRoles(
        isSuperadmin
          ? allRoles
          : allRoles.filter((r) => r.tenant_id === myTenant),
      );
      setTenantApps(
        tenantAppsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
      );
      setLoading(false);
    } catch (e) {
      console.error("Error cargando datos admin:", e);
      setLoading(false);
    }
  }
  
  async function handleSendInvitation() {
    if (!inviteData.email) {
      showMsg("Ingresá un email", "error");
      return;
    }
    const appsSelected = Object.entries(inviteData.apps)
      .filter(([, v]) => v.enabled)
      .map(([appId, v]) => ({ app_id: appId, role: v.role || "user" }));
    if (!appsSelected.length) {
      showMsg("Seleccioná al menos una app", "error");
      return;
    }

    setSaving(true);
    try {
      const res = await createInvitationFn({
        email: inviteData.email.toLowerCase().trim(),
        role_platform: inviteData.role_platform,
        tenant_id: inviteData.tenant_id,
        apps: appsSelected,
      });
      const token = res.data.token;

      const link = `${window.location.origin}/invitacion?token=${token}`;
      try {
        await navigator.clipboard.writeText(link);
        showMsg(
          "Invitación creada — link copiado al portapapeles ✓",
          "success",
        );
      } catch {
        showMsg(
          "Invitación creada ✓ (copiá el link manualmente: " + link + ")",
          "success",
        );
      }
      setShowInviteForm(false);
      setInviteData({
        email: "",
        role_platform: "user",
        tenant_id: "laugero",
        apps: {},
      });
      loadData();
    } catch (e) {
      showMsg("Error: " + e.message, "error");
    }
    setSaving(false);
  }

  async function handleResend(inv) {
    try {
      const res = await createInvitationFn({
        email: inv.email,
        role_platform: inv.role_platform,
        tenant_id: inv.tenant_id,
        apps: inv.apps,
      });
      const token = res.data.token;
      await cancelInvitationFn({ token: inv.token, motivo: "reemplazada" });
      const link = `${window.location.origin}/invitacion?token=${token}`;
      try {
        await navigator.clipboard.writeText(link);
        showMsg("Nuevo link copiado al portapapeles ✓", "success");
      } catch {
        showMsg("Nuevo link generado ✓ — copiá: " + link, "success");
      }
      loadData();
    } catch (e) {
      showMsg("Error: " + e.message, "error");
    }
  }

  async function handleCopyLink(token) {
    const link = `${window.location.origin}/invitacion?token=${token}`;
    await navigator.clipboard.writeText(link);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(""), 2000);
  }

  async function handleCancelInvitation(token) {
    if (
      !window.confirm(
        "¿Anular esta invitación? Queda registrada en el historial.",
      )
    )
      return;
    try {
      await cancelInvitationFn({ token, motivo: "cancelada" });
      showMsg("Invitación anulada ✓", "success");
      loadData();
    } catch (e) {
      showMsg("Error al anular: " + e.message, "error");
    }
  }

  function fechaCorta(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  function toggleAppInvite(appId) {
    setInviteData((prev) => ({
      ...prev,
      apps: {
        ...prev.apps,
        [appId]: prev.apps[appId]?.enabled
          ? { enabled: false }
          : { enabled: true, role: "user" },
      },
    }));
  }

  function setAppRole(appId, role) {
    setInviteData((prev) => ({
      ...prev,
      apps: { ...prev.apps, [appId]: { enabled: true, role } },
    }));
  }
  // Carga apps y obras del usuario PARA UNA EMPRESA concreta. Un usuario
  // multi-empresa tiene un juego de permisos por empresa, así que hay que
  // saber cuál se está editando antes de mostrar nada.
  async function cargarPermisosDeEmpresa(userId, tenantCtx) {
    const currentRoles = userAppRoles.filter(
      (r) => r.user_id === userId && r.tenant_id === tenantCtx,
    );
    const appsState = {};
    currentRoles.forEach((r) => {
      appsState[r.app_id] = { enabled: r.enabled, role: r.role };
    });
    setEditApps(appsState);
    try {
      const snap = await getDoc(doc(db, "user_obras", `${userId}_${tenantCtx}`));
      if (snap.exists()) {
        const d = snap.data();
        setEditObrasModo(d.modo || "todas");
        setEditObrasSel(d.obras || []);
      } else {
        setEditObrasModo("todas");
        setEditObrasSel([]);
      }
    } catch {
      setEditObrasModo("todas");
      setEditObrasSel([]);
    }
  }

  async function cambiarEmpresaEditada(userId, tenantCtx) {
    setEditTenantCtx(tenantCtx);
    await cargarPermisosDeEmpresa(userId, tenantCtx);
  }

  async function startEditApps(userId) {
    if (obras.length === 0) loadObras();
    const u = users.find((x) => x.id === userId);
    setEditUserRole(u?.role_platform || "user");
    // Empresas del usuario (multi-empresa). Si nunca se asignaron, es la principal.
    setEditTenantIds(
      u?.tenant_ids && u.tenant_ids.length > 0
        ? u.tenant_ids
        : u?.tenant_id
          ? [u.tenant_id]
          : [],
    );
    // Se empieza editando la empresa principal.
    const ctx = u?.tenant_id || null;
    setEditTenantCtx(ctx);
    if (ctx) await cargarPermisosDeEmpresa(userId, ctx);
    setEditingUser(userId);
  }

  // Solo superadmin. La empresa principal no se puede desmarcar: es el fallback
  // de las reglas y de todo el código que todavía lee tenant_id.
  function toggleEditTenant(tenantId, esPrincipal) {
    if (esPrincipal) return;
    setEditTenantIds((prev) =>
      prev.includes(tenantId)
        ? prev.filter((t) => t !== tenantId)
        : [...prev, tenantId],
    );
  }

  function toggleEditApp(appId) {
    setEditApps((prev) => ({
      ...prev,
      [appId]: prev[appId]?.enabled
        ? { enabled: false }
        : { enabled: true, role: prev[appId]?.role || "user" },
    }));
  }

  async function handleSaveApps(userId) {
    setSavingApps(true);
    try {
      // Restricción: tenant_admin no puede modificar superadmins ni autoasignarse superadmin
      const targetUser = users.find((u) => u.id === userId);
      if (!isSuperadmin && targetUser?.role_platform === "superadmin") {
        showMsg("No podés modificar un superadmin", "error");
        setSavingApps(false);
        return;
      }

      // Guardar cambio de rol de plataforma si cambió
      if (editUserRole !== targetUser?.role_platform) {
        if (!isSuperadmin) {
          showMsg(
            "Solo el superadmin puede modificar roles de plataforma",
            "error",
          );
          setSavingApps(false);
          return;
        }
        await setDoc(
          doc(db, "users", userId),
          { role_platform: editUserRole, updated_at: new Date().toISOString() },
          { merge: true },
        );
      }

      // Apps que el caller puede gestionar:
      // - superadmin: todas
      // - tenant_admin: solo donde ÉL es admin (no puede tocar apps que no administra, ej. meetings)
      // El uid viene del objeto de Auth: userProfile es el documento de Firestore
      // y el uid es su ID, no un campo adentro (userDoc.data() no lo incluye).
      const callerUid = user?.uid;
      const appsAdminDelCaller = new Set(
        userAppRoles
          .filter(
            (r) => r.user_id === callerUid && r.enabled && r.role === "admin",
          )
          .map((r) => r.app_id),
      );
      const appsGuardables = apps
        .filter((a) => a.active)
        .filter((a) => isSuperadmin || appsAdminDelCaller.has(a.id));

      for (const app of appsGuardables) {
        const appState = editApps[app.id];
        await setUserAppRoleFn({
          target_uid: userId,
          app_id: app.id,
          role: appState?.role || "user",
          enabled: !!appState?.enabled,
          tenant_id: editTenantCtx || undefined,
        });
      }
      // Guardar empresas del usuario (solo superadmin puede)
      if (isSuperadmin && editTenantIds.length > 0) {
        await setUserTenantsFn({
          target_uid: userId,
          tenant_ids: editTenantIds,
        });
      }
      // Guardar asignación de obras
      await setUserObrasFn({
        target_uid: userId,
        modo: editObrasModo,
        obras: editObrasModo === "seleccion" ? editObrasSel : [],
        tenant_id: editTenantCtx || undefined,
      });
      // Si no se pudo tocar ninguna app, avisar en vez de felicitar: un éxito
      // falso hace creer que el cambio quedó hecho cuando no se intentó nada.
      if (appsGuardables.length === 0) {
        showMsg(
          "No se guardaron apps: no sos admin de ninguna app de este usuario.",
          "error",
        );
      } else {
        showMsg("Apps actualizadas correctamente", "success");
      }
      setEditingUser(null);
      loadData();
    } catch (e) {
      showMsg("Error: " + e.message, "error");
    }
    setSavingApps(false);
  }
  
  function startEditTenant(t) {
    setEditTenantData({
      name: t.name || "",
      plan: t.plan || "basic",
      primaryColor: t.primaryColor || "#2563eb",
      secondaryColor: t.secondaryColor || "#f1f5f9",
      logoText: t.logoText || "",
      logoSubtext: t.logoSubtext || "",
      logoUrl: t.logoUrl || "",
    });
    const tApps = {};
    tenantApps
      .filter((ta) => ta.tenant_id === t.id)
      .forEach((ta) => {
        tApps[ta.app_id] = ta.enabled;
      });
    setEditTenantApps(tApps);
    setEditingTenant(t.id);
  }
  async function loadObras() {
    setObrasLoading(true);
    try {
      const q = isSuperadmin
        ? query(collection(db, "obras"), orderBy("nombre"))
        : query(
            collection(db, "obras"),
            where("tenant_id", "==", userProfile?.tenant_id),
            orderBy("nombre"),
          );
      const snap = await getDocs(q);
      setObras(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Error cargando obras:", e);
    }
    setObrasLoading(false);
  }

  async function handleCrearObra() {
    if (!obraData.nombre.trim()) {
      showMsg("El nombre de la obra es obligatorio", "error");
      return;
    }
    const tenantId = isSuperadmin ? obraData.tenant_id : userProfile?.tenant_id;
    if (!tenantId) {
      showMsg("Seleccioná una empresa", "error");
      return;
    }
    setSavingObra(true);
    try {
      await setDoc(doc(collection(db, "obras")), {
        nombre: obraData.nombre.trim(),
        direccion: obraData.direccion.trim(),
        comitente: obraData.comitente.trim(),
        tipo_obra: obraData.tipo_obra,
        tenant_id: tenantId,
        estado: "activa",
        creado_at: new Date().toISOString(),
        creado_por: user.email,
      });
      showMsg("Obra creada correctamente", "success");
      setShowObraForm(false);
      setObraData({
        nombre: "",
        direccion: "",
        tipo_obra: "VIVIENDAS_UNIFAMILIARES",
        tenant_id: "",
      });
      loadObras();
    } catch (e) {
      showMsg("Error al crear la obra: " + e.message, "error");
    }
    setSavingObra(false);
  }
  async function cargarAppObras() {
    try {
      const snap = await getDocs(collection(db, "app_obras"));
      const data = {};
      snap.docs.forEach((d) => {
        data[d.id] = { id: d.id, ...d.data() };
      });
      setAppObras(data);
    } catch (e) {
      console.error("Error cargando app_obras:", e);
    }
  }

  async function toggleObraEnApp(obraId, tenantId, appId, habilitada) {
    const docId = `${tenantId}_${appId}`;
    try {
      const ref = doc(db, "app_obras", docId);
      const config = appObras[docId];
      const modoActual = config?.modo || "todas";
      let obrasList = config?.obras_habilitadas || [];

      if (modoActual === "todas" && !habilitada) {
        // Pasar a selección: todas las obras del tenant menos la que se desmarca
        const todasLasObras = obras
          .filter((o) => o.tenant_id === tenantId)
          .map((o) => o.id)
          .filter((id) => id !== obraId);
        await setDoc(ref, {
          tenant_id: tenantId,
          app_id: appId,
          modo: "seleccion",
          obras_habilitadas: todasLasObras,
        });
      } else if (modoActual === "seleccion") {
        const nuevaLista = habilitada
          ? [...obrasList, obraId]
          : obrasList.filter((id) => id !== obraId);
        const todasActivas = obras
          .filter((o) => o.tenant_id === tenantId)
          .map((o) => o.id);
        const todasHabilitadas = todasActivas.every((id) =>
          nuevaLista.includes(id),
        );
        await setDoc(
          ref,
          {
            tenant_id: tenantId,
            app_id: appId,
            modo: todasHabilitadas ? "todas" : "seleccion",
            obras_habilitadas: todasHabilitadas ? [] : nuevaLista,
          },
          { merge: true },
        );
      } else {
        await setDoc(ref, {
          tenant_id: tenantId,
          app_id: appId,
          modo: "todas",
          obras_habilitadas: [],
        });
      }
      await cargarAppObras();
    } catch (e) {
      console.error("Error actualizando app_obras:", e);
    }
  }
  async function handleGuardarEditObra() {
    if (!editObraData.nombre.trim()) {
      showMsg("El nombre es obligatorio", "error");
      return;
    }
    setSavingEditObra(true);
    try {
      await setDoc(
        doc(db, "obras", editingObra),
        {
          nombre: editObraData.nombre.trim(),
          direccion: editObraData.direccion.trim(),
          comitente: editObraData.comitente.trim(),
          tipo_obra: editObraData.tipo_obra,
          actualizado_at: new Date().toISOString(),
        },
        { merge: true },
      );
      showMsg("Obra actualizada correctamente", "success");
      setEditingObra(null);
      loadObras();
    } catch (e) {
      showMsg("Error al actualizar la obra", "error");
    }
    setSavingEditObra(false);
  }

  async function handleCambiarEstadoObra(obraId, nuevoEstado) {
    try {
      await setDoc(
        doc(db, "obras", obraId),
        { estado: nuevoEstado, actualizado_at: new Date().toISOString() },
        { merge: true },
      );
      loadObras();
    } catch (e) {
      showMsg("Error al actualizar la obra", "error");
    }
  }
  async function handleExportTenant(tenantId, tenantName) {
    try {
      const summary = await exportTenantData(tenantId, tenantName);
      const total = Object.values(summary.collections).reduce(
        (a, b) => a + b,
        0,
      );
      alert(`Exportación completa. ${total} registros descargados en ZIP.`);
    } catch (e) {
      alert("Error al exportar: " + e.message);
    }
  }
  async function handleSaveTenant(tenantId) {
    setSavingTenant(true);
    try {
      await setDoc(
        doc(db, "tenants", tenantId),
        {
          ...editTenantData,
          logoUrl: editTenantData.logoUrl || "",
          active: true,
          updated_at: new Date().toISOString(),
        },
        { merge: true },
      );

      for (const app of apps.filter((a) => a.active)) {
        const taId = `${tenantId}__${app.id}`;
        if (editTenantApps[app.id]) {
          await setDoc(doc(db, "tenant_apps", taId), {
            tenant_id: tenantId,
            app_id: app.id,
            enabled: true,
          });
        } else {
          await deleteDoc(doc(db, "tenant_apps", taId));
        }
      }

      showMsg("Empresa actualizada correctamente", "success");
      setEditingTenant(null);
      loadData();
    } catch (e) {
      showMsg("Error: " + e.message, "error");
    }
    setSavingTenant(false);
  }
  async function handleCreateTenant() {
    if (!tenantData.id || !tenantData.name) {
      showMsg("Completá ID y nombre de la empresa", "error");
      return;
    }
    if (!tenantData.adminEmail) {
      showMsg("Designá un email de administrador", "error");
      return;
    }
    if (!/^[a-z0-9-]+$/.test(tenantData.id)) {
      showMsg(
        "El ID solo puede tener letras minúsculas, números y guiones",
        "error",
      );
      return;
    }
    if (tenants.find((t) => t.id === tenantData.id)) {
      showMsg("Ya existe una empresa con ese ID", "error");
      return;
    }

    setSavingTenant(true);
    try {
      // 1. Crear tenant
      await setDoc(doc(db, "tenants", tenantData.id), {
        name: tenantData.name,
        plan: tenantData.plan,
        active: true,
        primaryColor: tenantData.primaryColor,
        secondaryColor: tenantData.secondaryColor,
        logoText: tenantData.logoText || tenantData.name.toUpperCase(),
        logoSubtext: tenantData.logoSubtext,
        logoUrl: tenantData.logoUrl || "",
        created_at: new Date().toISOString(),
      });

      // 2. Crear invitación automática para el admin
      const selectedApps = Object.entries(tenantData.enabledApps || {})
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (!selectedApps.length) {
        showMsg("Seleccioná al menos una app para la empresa", "error");
        setSavingTenant(false);
        return;
      }
      // Asignar al admin las apps seleccionadas
      const tenantAppsList = selectedApps.map((appId) => ({
        app_id: appId,
        role: "admin",
      }));

      const resTenant = await createInvitationFn({
        email: tenantData.adminEmail.toLowerCase().trim(),
        role_platform: "tenant_admin",
        tenant_id: tenantData.id,
        apps: tenantAppsList,
      });
      const token = resTenant.data.token;

      /// 3. Crear tenant_apps para las apps seleccionadas

      for (const appId of selectedApps) {
        await setDoc(doc(db, "tenant_apps", `${tenantData.id}__${appId}`), {
          tenant_id: tenantData.id,
          app_id: appId,
          enabled: true,
        });
      }

      const link = `${window.location.origin}/invitacion?token=${token}`;
      try {
        await navigator.clipboard.writeText(link);
        showMsg(
          `Empresa creada — invitación para el admin copiada al portapapeles`,
          "success",
        );
      } catch {
        showMsg(
          `Empresa creada ✓ — copiá la invitación del admin: ` + link,
          "success",
        );
      }
      setShowTenantForm(false);
      setTenantData({
        id: "",
        name: "",
        plan: "basic",
        primaryColor: "#2563eb",
        secondaryColor: "#f1f5f9",
        logoText: "",
        logoSubtext: "",
        adminEmail: "",
      });
      loadData();
    } catch (e) {
      showMsg("Error: " + e.message, "error");
    }
    setSavingTenant(false);
  }
  function showMsg(text, type) {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  }

  function hoursLeft(inv) {
    const diff = new Date(inv.expires_at) - new Date();
    const hours = Math.floor(diff / 3600000);
    return hours > 0 ? `Expira en ${hours}h` : "Expirada";
  }

  if (loading)
    return <p style={{ textAlign: "center", marginTop: 40 }}>Cargando...</p>;

  // Una invitación "pending" con expires_at pasado está VENCIDA. Se calcula acá
  // en vez de marcarla en la base: evita depender de un proceso nocturno.
  const invVencida = (i) =>
    i.status === "pending" && new Date(i.expires_at) < new Date();

  const pendingInvitations = invitations.filter(
    (i) => i.status === "pending" && !invVencida(i),
  );
  const acceptedInvitations = invitations.filter(
    (i) => i.status === "accepted",
  );
  // Historial: anuladas + vencidas, más recientes primero
  const historialInvitations = invitations
    .filter((i) => i.status === "cancelled" || invVencida(i))
    .sort(
      (a, b) =>
        new Date(b.cancelled_at || b.expires_at || 0) -
        new Date(a.cancelled_at || a.expires_at || 0),
    );

  // ── Filtros de búsqueda por sección ──
  const nombreTenant = (tid) =>
    tenants.find((t) => t.id === tid)?.name || tid;

  const tenantsFiltrados = tenants.filter((t) =>
    coincide(qTenants, t.name, t.id, t.plan),
  );
  const usersFiltrados = users.filter((u) =>
    coincide(
      qUsers,
      u.display_name,
      u.email,
      u.tenant_id,
      nombreTenant(u.tenant_id),
      u.role_platform,
    ),
  );
  const appsFiltradas = apps.filter((a) =>
    coincide(qApps, a.name, a.id, a.type, a.url),
  );
  const obrasFiltradas = obras.filter((o) =>
    coincide(
      qObras,
      o.nombre,
      o.comitente,
      o.direccion,
      o.tipo_obra,
      o.tenant_id,
      nombreTenant(o.tenant_id),
    ),
  );
  const filtroInv = (i) =>
    coincide(qInvitations, i.email, i.tenant_id, nombreTenant(i.tenant_id));
  const pendingFiltradas = pendingInvitations.filter(filtroInv);
  const acceptedFiltradas = acceptedInvitations.filter(filtroInv);
  const historialFiltradas = historialInvitations.filter(filtroInv);

  return (
    <div
      style={{
        ...styles.container,
        maxWidth: activeTab === "audit" ? 1400 : 1000,
      }}
    >
      <style>{`@keyframes slideInToast { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <div style={styles.header}>
        <h1 style={styles.title}>Panel de Administración</h1>
        <p style={styles.subtitle}>
          Gestión de usuarios, invitaciones y aplicaciones
        </p>
      </div>

      {message.text && (
        <div
          style={{
            ...styles.message,
            backgroundColor: message.type === "error" ? "#fef2f2" : "#f0fdf4",
            color: message.type === "error" ? "#dc2626" : "#16a34a",
          }}
        >
          {message.text}
        </div>
      )}

      <div style={styles.tabs}>
        {[
          ...(isSuperadmin ? ["tenants"] : []),
          "users",
          "invitations",
          "apps",
          "obras",
          "audit",
          ...(isSuperadmin ? ["integraciones"] : []),
        ].map((tab) => (
          <button
            key={tab}
            style={{
              ...styles.tab,
              ...(activeTab === tab ? styles.tabActive : {}),
            }}
            onClick={() => {
              setActiveTab(tab);
              if (tab === "obras") {
                loadObras();
                cargarAppObras();
              }
            }}
          >
            {tab === "tenants"
              ? "🏢 Empresas"
              : tab === "users"
                ? "👥 Usuarios"
                : tab === "invitations"
                  ? "✉️ Invitaciones"
                  : tab === "apps"
                    ? "📦 Apps"
                    : tab === "obras"
                      ? "🏗️ Obras"
                      : tab === "audit"
                        ? "🔍 Auditoría"
                        : "🔑 Integraciones"}
          </button>
        ))}
      </div>

      {/* ── USUARIOS ── */}
      {activeTab === "tenants" && isSuperadmin && (
        <div>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Empresas ({tenants.length})</h2>
            <button
              style={styles.btnPrimary}
              onClick={() => setShowTenantForm(!showTenantForm)}
            >
              {showTenantForm ? "Cancelar" : "+ Nueva empresa"}
            </button>
          </div>

          {showTenantForm && (
            <div style={styles.formCard}>
              <h3 style={styles.formTitle}>Nueva empresa</h3>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>ID de la empresa *</label>
                  <input
                    style={styles.input}
                    placeholder="mi-constructora"
                    value={tenantData.id}
                    onChange={(e) =>
                      setTenantData({
                        ...tenantData,
                        id: e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, ""),
                      })
                    }
                  />
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>
                    Solo letras, números y guiones
                  </span>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nombre de la empresa *</label>
                  <input
                    style={styles.input}
                    placeholder="Mi Constructora S.A."
                    value={tenantData.name}
                    onChange={(e) =>
                      setTenantData({ ...tenantData, name: e.target.value })
                    }
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Plan</label>
                  <select
                    style={styles.input}
                    value={tenantData.plan}
                    onChange={(e) =>
                      setTenantData({ ...tenantData, plan: e.target.value })
                    }
                  >
                    <option value="basic">Basic</option>
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>

              <div style={{ ...styles.formGrid, marginTop: 12 }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email del administrador *</label>
                  <input
                    style={styles.input}
                    type="email"
                    placeholder="admin@empresa.com"
                    value={tenantData.adminEmail}
                    onChange={(e) =>
                      setTenantData({
                        ...tenantData,
                        adminEmail: e.target.value,
                      })
                    }
                  />
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>
                    Recibirá invitación como admin
                  </span>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Texto del logo</label>
                  <input
                    style={styles.input}
                    placeholder="MI CONSTRUCTORA"
                    value={tenantData.logoText}
                    onChange={(e) =>
                      setTenantData({ ...tenantData, logoText: e.target.value })
                    }
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Subtexto del logo</label>
                  <input
                    style={styles.input}
                    placeholder="Construcciones & Desarrollos"
                    value={tenantData.logoSubtext}
                    onChange={(e) =>
                      setTenantData({
                        ...tenantData,
                        logoSubtext: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div
                style={{
                  ...styles.formGrid,
                  marginTop: 12,
                  gridTemplateColumns: "1fr 1fr",
                }}
              >
                <div style={styles.formGroup}>
                  <label style={styles.label}>Color principal</label>
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <input
                      type="color"
                      value={tenantData.primaryColor}
                      onChange={(e) =>
                        setTenantData({
                          ...tenantData,
                          primaryColor: e.target.value,
                        })
                      }
                      style={{
                        width: 40,
                        height: 36,
                        border: "1px solid #e2e8f0",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    />
                    <input
                      style={{ ...styles.input, flex: 1 }}
                      value={tenantData.primaryColor}
                      onChange={(e) =>
                        setTenantData({
                          ...tenantData,
                          primaryColor: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Color secundario</label>
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <input
                      type="color"
                      value={tenantData.secondaryColor}
                      onChange={(e) =>
                        setTenantData({
                          ...tenantData,
                          secondaryColor: e.target.value,
                        })
                      }
                      style={{
                        width: 40,
                        height: 36,
                        border: "1px solid #e2e8f0",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    />
                    <input
                      style={{ ...styles.input, flex: 1 }}
                      value={tenantData.secondaryColor}
                      onChange={(e) =>
                        setTenantData({
                          ...tenantData,
                          secondaryColor: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <label style={styles.label}>
                  Apps habilitadas para esta empresa
                </label>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  {apps
                    .filter((a) => a.active)
                    .map((app) => {
                      const checked = tenantData.enabledApps?.[app.id] || false;
                      return (
                        <label
                          key={app.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "8px 14px",
                            border: `2px solid ${checked ? "#2563eb" : "#e2e8f0"}`,
                            backgroundColor: checked ? "#eff6ff" : "white",
                            borderRadius: 8,
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              setTenantData((prev) => ({
                                ...prev,
                                enabledApps: {
                                  ...prev.enabledApps,
                                  [app.id]: !checked,
                                },
                              }))
                            }
                            style={{
                              accentColor: "#2563eb",
                              width: 16,
                              height: 16,
                            }}
                          />
                          {app.name}
                        </label>
                      );
                    })}
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button
                  style={styles.btnPrimary}
                  onClick={handleCreateTenant}
                  disabled={savingTenant}
                >
                  {savingTenant
                    ? "Creando..."
                    : "Crear empresa e invitar admin"}
                </button>
                <button
                  style={styles.btnSecondary}
                  onClick={() => setShowTenantForm(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          <Buscador
            value={qTenants}
            onChange={setQTenants}
            placeholder="Buscar empresa por nombre, ID o plan..."
            resultados={tenantsFiltrados.length}
            total={tenants.length}
          />

          <div style={styles.table}>
            <div
              style={{
                ...styles.tableRow,
                ...styles.tableHeader,
                gridTemplateColumns: "1fr 1fr 100px 100px",
              }}
            >
              <span>Empresa</span>
              <span>ID</span>
              <span>Plan</span>
              <span>Estado</span>
            </div>
            {tenantsFiltrados.map((t) => {
              const isExp = expandedTenant === t.id;
              const isEditing = editingTenant === t.id;
              const tApps = tenantApps.filter(
                (ta) => ta.tenant_id === t.id && ta.enabled,
              );
              return (
                <div key={t.id}>
                  <div
                    style={{
                      ...styles.tableRow,
                      gridTemplateColumns: "1fr 1fr 100px 100px",
                      cursor: "pointer",
                      backgroundColor: isExp ? "#f8fafc" : "white",
                    }}
                    onClick={() => setExpandedTenant(isExp ? null : t.id)}
                  >
                    <span style={styles.bold}>
                      {isExp ? "\u25BC" : "\u25B6"} {t.name}
                    </span>
                    <span style={styles.muted}>{t.id}</span>
                    <span style={styles.badge}>{t.plan}</span>
                    <span
                      style={{
                        ...styles.statusBadge,
                        backgroundColor: t.active ? "#f0fdf4" : "#fef2f2",
                        color: t.active ? "#16a34a" : "#dc2626",
                      }}
                    >
                      {t.active ? "Activa" : "Inactiva"}
                    </span>
                  </div>
                  {isExp && (
                    <div
                      style={{
                        padding: "12px 20px 16px 40px",
                        backgroundColor: "#f8fafc",
                        borderTop: "1px solid #e2e8f0",
                      }}
                    >
                      <p
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: 8,
                        }}
                      >
                        Apps habilitadas ({tApps.length})
                      </p>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          marginBottom: 12,
                        }}
                      >
                        {tApps.map((ta) => {
                          const appInfo = apps.find((a) => a.id === ta.app_id);
                          return (
                            <span
                              key={ta.id}
                              style={{
                                padding: "6px 12px",
                                backgroundColor: "white",
                                borderRadius: 8,
                                border: "1px solid #e2e8f0",
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#1e293b",
                              }}
                            >
                              {appInfo?.name || ta.app_id}
                            </span>
                          );
                        })}
                        {tApps.length === 0 && (
                          <span
                            style={{
                              fontSize: 13,
                              color: "#94a3b8",
                              fontStyle: "italic",
                            }}
                          >
                            Sin apps asignadas
                          </span>
                        )}
                      </div>

                      {isEditing ? (
                        <div style={{ marginTop: 12 }}>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr 1fr",
                              gap: 12,
                            }}
                          >
                            <div style={styles.formGroup}>
                              <label style={styles.label}>Nombre</label>
                              <input
                                style={styles.input}
                                value={editTenantData.name}
                                onChange={(e) =>
                                  setEditTenantData({
                                    ...editTenantData,
                                    name: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <div style={styles.formGroup}>
                              <label style={styles.label}>Plan</label>
                              <select
                                style={styles.input}
                                value={editTenantData.plan}
                                onChange={(e) =>
                                  setEditTenantData({
                                    ...editTenantData,
                                    plan: e.target.value,
                                  })
                                }
                              >
                                <option value="basic">Basic</option>
                                <option value="pro">Pro</option>
                                <option value="enterprise">Enterprise</option>
                              </select>
                            </div>
                            <div style={styles.formGroup}>
                              <label style={styles.label}>Texto del logo</label>
                              <input
                                style={styles.input}
                                value={editTenantData.logoText}
                                onChange={(e) =>
                                  setEditTenantData({
                                    ...editTenantData,
                                    logoText: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr 1fr",
                              gap: 12,
                              marginTop: 12,
                            }}
                          >
                            <div style={styles.formGroup}>
                              <label style={styles.label}>
                                Subtexto del logo
                              </label>
                              <input
                                style={styles.input}
                                value={editTenantData.logoSubtext}
                                onChange={(e) =>
                                  setEditTenantData({
                                    ...editTenantData,
                                    logoSubtext: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <div style={styles.formGroup}>
                              <label style={styles.label}>
                                Color principal
                              </label>
                              <div style={{ display: "flex", gap: 8 }}>
                                <input
                                  type="color"
                                  value={editTenantData.primaryColor}
                                  onChange={(e) =>
                                    setEditTenantData({
                                      ...editTenantData,
                                      primaryColor: e.target.value,
                                    })
                                  }
                                  style={{
                                    width: 40,
                                    height: 36,
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 6,
                                    cursor: "pointer",
                                  }}
                                />
                                <input
                                  style={{ ...styles.input, flex: 1 }}
                                  value={editTenantData.primaryColor}
                                  onChange={(e) =>
                                    setEditTenantData({
                                      ...editTenantData,
                                      primaryColor: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </div>
                            <div style={styles.formGroup}>
                              <label style={styles.label}>
                                Color secundario
                              </label>
                              <div style={{ display: "flex", gap: 8 }}>
                                <input
                                  type="color"
                                  value={editTenantData.secondaryColor}
                                  onChange={(e) =>
                                    setEditTenantData({
                                      ...editTenantData,
                                      secondaryColor: e.target.value,
                                    })
                                  }
                                  style={{
                                    width: 40,
                                    height: 36,
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 6,
                                    cursor: "pointer",
                                  }}
                                />
                                <input
                                  style={{ ...styles.input, flex: 1 }}
                                  value={editTenantData.secondaryColor}
                                  onChange={(e) =>
                                    setEditTenantData({
                                      ...editTenantData,
                                      secondaryColor: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                          <LogoUploader
                            tenantId={editingTenant}
                            currentUrl={editTenantData.logoUrl}
                            onUploaded={(url) =>
                              setEditTenantData((prev) => ({
                                ...prev,
                                logoUrl: url,
                              }))
                            }
                            onRemove={() =>
                              setEditTenantData((prev) => ({
                                ...prev,
                                logoUrl: "",
                              }))
                            }
                          />

                          <div style={{ marginTop: 12 }}>
                            <label style={styles.label}>Apps habilitadas</label>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 8,
                                marginTop: 8,
                              }}
                            >
                              {apps
                                .filter((a) => a.active)
                                .map((app) => {
                                  const checked = !!editTenantApps[app.id];
                                  return (
                                    <label
                                      key={app.id}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "8px 14px",
                                        border: `2px solid ${checked ? "#2563eb" : "#e2e8f0"}`,
                                        backgroundColor: checked
                                          ? "#eff6ff"
                                          : "white",
                                        borderRadius: 8,
                                        cursor: "pointer",
                                        fontSize: 13,
                                        fontWeight: 600,
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() =>
                                          setEditTenantApps((prev) => ({
                                            ...prev,
                                            [app.id]: !checked,
                                          }))
                                        }
                                        style={{
                                          accentColor: "#2563eb",
                                          width: 16,
                                          height: 16,
                                        }}
                                      />
                                      {app.name}
                                    </label>
                                  );
                                })}
                            </div>
                          </div>

                          <div
                            style={{ display: "flex", gap: 8, marginTop: 16 }}
                          >
                            <button
                              style={styles.btnPrimary}
                              onClick={() => handleSaveTenant(t.id)}
                              disabled={savingTenant}
                            >
                              {savingTenant
                                ? "Guardando..."
                                : "Guardar cambios"}
                            </button>
                            <button
                              style={styles.btnSecondary}
                              onClick={() => setEditingTenant(null)}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                          <button
                            style={{ ...styles.btnSmall }}
                            onClick={() => startEditTenant(t)}
                          >
                            Editar empresa
                          </button>
                          <button
                            style={{
                              ...styles.btnSmall,
                              background: "#16A34A",
                              color: "#fff",
                            }}
                            onClick={() => handleExportTenant(t.id, t.name)}
                          >
                            ⬇ Exportar datos
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {activeTab === "users" && (
        <div>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              Usuarios activos ({users.length})
            </h2>
            <button
              style={styles.btnPrimary}
              onClick={() => {
                setShowInviteForm(true);
                setActiveTab("invitations");
              }}
            >
              + Invitar usuario
            </button>
          </div>
          <Buscador
            value={qUsers}
            onChange={setQUsers}
            placeholder="Buscar usuario por nombre, email, empresa o rol..."
            resultados={usersFiltrados.length}
            total={users.length}
          />

          <div style={styles.table}>
            <div style={{ ...styles.tableRow, ...styles.tableHeader }}>
              <span>Nombre</span>
              <span>Email</span>
              <span>Empresa</span>
              <span>Rol plataforma</span>
              <span>Estado</span>
            </div>
            {usersFiltrados.map((u) => {
              const isExpanded = expandedUser === u.id;
              const userRoles = userAppRoles.filter(
                (r) =>
                  r.user_id === u.id &&
                  tenantApps.some(
                    (ta) =>
                      ta.app_id === r.app_id &&
                      ta.tenant_id === r.tenant_id &&
                      ta.enabled,
                  ),
              );
              return (
                <div key={u.id}>
                  <div
                    style={{
                      ...styles.tableRow,
                      cursor: "pointer",
                      backgroundColor: isExpanded ? "#f8fafc" : "white",
                    }}
                    onClick={() => setExpandedUser(isExpanded ? null : u.id)}
                  >
                    <span style={styles.bold}>
                      {isExpanded ? "\u25BC" : "\u25B6"}{" "}
                      {u.display_name || "\u2014"}
                    </span>
                    <span style={styles.muted}>{u.email}</span>
                    <span style={styles.muted}>
                      {tenants.find((t) => t.id === u.tenant_id)?.name ||
                        u.tenant_id}
                    </span>
                    <span style={styles.badge}>
                      {u.role_platform || u.role || "user"}
                    </span>
                    <span
                      style={{
                        ...styles.statusBadge,
                        backgroundColor:
                          u.status === "active" ? "#f0fdf4" : "#fef9c3",
                        color: u.status === "active" ? "#16a34a" : "#ca8a04",
                      }}
                    >
                      {u.status || "active"}
                    </span>
                  </div>
                  {isExpanded && (
                    <div
                      style={{
                        padding: "12px 20px 16px 40px",
                        backgroundColor: "#f8fafc",
                        borderTop: "1px solid #e2e8f0",
                      }}
                    >
                      {editingUser !== u.id && (
                        <>
                        <p
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#64748b",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            marginBottom: 8,
                          }}
                        >
                          Apps asignadas ({userRoles.length})
                        </p>
                        {userRoles.length === 0 ? (
                          <p
                            style={{
                              fontSize: 13,
                              color:
                                u.role_platform === "superadmin"
                                  ? "#2563eb"
                                  : "#94a3b8",
                              fontStyle: "italic",
                            }}
                          >
                            {u.role_platform === "superadmin"
                              ? "Acceso total a todas las apps (superadmin)"
                              : "Sin apps asignadas"}
                          </p>
                        ) : (
                          <div
                            style={{ display: "flex", flexWrap: "wrap", gap: 8 }}
                          >
                            {userRoles.map((role) => {
                              const appInfo = apps.find(
                                (a) => a.id === role.app_id,
                              );
                              return (
                                <div
                                  key={role.id}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "8px 14px",
                                    backgroundColor: "white",
                                    borderRadius: 8,
                                    border: "1px solid #e2e8f0",
                                    fontSize: 13,
                                  }}
                                >
                                  <span
                                    style={{ fontWeight: 600, color: "#1e293b" }}
                                  >
                                    {appInfo?.name || role.app_id}
                                  </span>
                                  {(u.tenant_ids || []).length > 1 && (
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: "#94a3b8",
                                        borderLeft: "1px solid #e2e8f0",
                                        paddingLeft: 8,
                                      }}
                                    >
                                      {tenants.find(
                                        (t) => t.id === role.tenant_id,
                                      )?.name || role.tenant_id}
                                    </span>
                                  )}
                                  <span
                                    style={{
                                      padding: "2px 8px",
                                      borderRadius: 99,
                                      fontSize: 11,
                                      fontWeight: 600,
                                      backgroundColor:
                                        role.role === "admin"
                                          ? "#fef2f2"
                                          : role.role === "manager"
                                            ? "#fef9c3"
                                            : "#f0fdf4",
                                      color:
                                        role.role === "admin"
                                          ? "#dc2626"
                                          : role.role === "manager"
                                            ? "#ca8a04"
                                            : "#16a34a",
                                    }}
                                  >
                                    {role.role}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        </>
                      )}

                      {editingUser === u.id ? (
                        <div style={{ marginTop: 16 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 16,
                              marginBottom: 12,
                            }}
                          >
                            {isSuperadmin && (
                              <div>
                                <p
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "#64748b",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    marginBottom: 6,
                                  }}
                                >
                                  Rol en la plataforma
                                </p>
                                <p
                                  style={{
                                    fontSize: 12,
                                    color: "#94a3b8",
                                    marginBottom: 6,
                                  }}
                                >
                                  Es uno solo por persona: aplica a todas sus
                                  empresas.
                                </p>
                                <select
                                  style={{
                                    padding: "6px 10px",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 6,
                                    fontSize: 13,
                                    fontFamily:
                                      "'Plus Jakarta Sans', sans-serif",
                                  }}
                                  value={editUserRole}
                                  onChange={(e) =>
                                    setEditUserRole(e.target.value)
                                  }
                                >
                                  <option value="user">Usuario</option>
                                  <option value="tenant_admin">
                                    Administrador
                                  </option>
                                  <option value="superadmin">Superadmin</option>
                                </select>
                              </div>
                            )}
                          </div>
                          {isSuperadmin && (
                            <>
                              <p
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "#64748b",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  marginBottom: 4,
                                }}
                              >
                                Empresas
                              </p>
                              <p
                                style={{
                                  fontSize: 12,
                                  color: "#94a3b8",
                                  marginBottom: 8,
                                }}
                              >
                                El usuario opera en una empresa a la vez y
                                cambia con el selector del inicio. La empresa
                                principal no se puede quitar.
                              </p>
                              {(() => {
                                // Empresas donde el usuario tiene permisos pero
                                // a las que NO pertenece: permisos huérfanos de
                                // configuraciones anteriores. No dan acceso
                                // (no puede activar esa empresa), pero conviene
                                // resolverlos para que el resumen no engañe.
                                const conPermisos = [
                                  ...new Set(
                                    userAppRoles
                                      .filter(
                                        (r) => r.user_id === u.id && r.enabled,
                                      )
                                      .map((r) => r.tenant_id),
                                  ),
                                ];
                                const huerfanas = conPermisos.filter(
                                  (tid) => !editTenantIds.includes(tid),
                                );
                                if (huerfanas.length === 0) return null;
                                const nombres = huerfanas
                                  .map(
                                    (tid) =>
                                      tenants.find((t) => t.id === tid)?.name ||
                                      tid,
                                  )
                                  .join(", ");
                                return (
                                  <div
                                    style={{
                                      padding: "10px 12px",
                                      marginBottom: 10,
                                      backgroundColor: "#fffbeb",
                                      border: "1px solid #fde68a",
                                      borderRadius: 8,
                                      fontSize: 12,
                                      color: "#92400e",
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    Tiene permisos en <b>{nombres}</b> pero no
                                    pertenece a esa empresa, así que hoy no le
                                    dan acceso. Si corresponde, marcala acá
                                    abajo y guardá; si no, marcala, guardá,
                                    entrá a esa empresa y sacale las apps.
                                  </div>
                                );
                              })()}
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 8,
                                  marginBottom: 16,
                                }}
                              >
                                {tenants.map((t) => {
                                  const u = users.find((x) => x.id === editingUser);
                                  const esPrincipal = u?.tenant_id === t.id;
                                  const marcada =
                                    editTenantIds.includes(t.id) || esPrincipal;
                                  return (
                                    <label
                                      key={t.id}
                                      title={
                                        esPrincipal
                                          ? "Empresa principal — no se puede quitar"
                                          : t.name
                                      }
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        padding: "6px 10px",
                                        border: "1px solid",
                                        borderColor: marcada
                                          ? "#1e293b"
                                          : "#e2e8f0",
                                        borderRadius: 8,
                                        cursor: esPrincipal
                                          ? "default"
                                          : "pointer",
                                        backgroundColor: marcada
                                          ? "#f8fafc"
                                          : "white",
                                        opacity: esPrincipal ? 0.75 : 1,
                                        fontSize: 13,
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={marcada}
                                        disabled={esPrincipal}
                                        onChange={() =>
                                          toggleEditTenant(t.id, esPrincipal)
                                        }
                                      />
                                      {t.name || t.id}
                                      {esPrincipal && (
                                        <span
                                          style={{
                                            fontSize: 10,
                                            color: "#94a3b8",
                                          }}
                                        >
                                          principal
                                        </span>
                                      )}
                                    </label>
                                  );
                                })}
                              </div>
                            </>
                          )}

                          <div
                            style={{
                              borderTop: "1px solid #e2e8f0",
                              marginTop: 20,
                              paddingTop: 16,
                              marginBottom: 14,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                flexWrap: "wrap",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "#64748b",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Permisos en
                              </span>
                              {editTenantIds.length > 1 ? (
                                <select
                                  value={editTenantCtx || ""}
                                  onChange={(e) =>
                                    cambiarEmpresaEditada(u.id, e.target.value)
                                  }
                                  style={{
                                    padding: "6px 10px",
                                    border: "1px solid #cbd5e1",
                                    borderRadius: 6,
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: "#1e293b",
                                    fontFamily: "inherit",
                                    cursor: "pointer",
                                  }}
                                >
                                  {editTenantIds.map((tid) => (
                                    <option key={tid} value={tid}>
                                      {tenants.find((t) => t.id === tid)?.name ||
                                        tid}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: "#1e293b",
                                  }}
                                >
                                  {tenants.find((t) => t.id === editTenantCtx)
                                    ?.name || editTenantCtx}
                                </span>
                              )}
                            </div>
                            {editTenantIds.length > 1 && (
                              <p
                                style={{
                                  fontSize: 12,
                                  color: "#94a3b8",
                                  marginTop: 6,
                                }}
                              >
                                Las apps y las obras de abajo son solo de esta
                                empresa. Cambiá el selector para editar otra.
                              </p>
                            )}
                          </div>

                          <p
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#64748b",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              marginBottom: 8,
                            }}
                          >
                            Apps y rol en esta empresa
                          </p>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                              marginBottom: 12,
                            }}
                          >
                            {apps
                              .filter((a) => {
                                if (!a.active) return false;
                                if (
                                  !tenantApps.some(
                                    (ta) =>
                                      ta.app_id === a.id &&
                                      ta.tenant_id === u.tenant_id &&
                                      ta.enabled,
                                  )
                                )
                                  return false;
                                if (isSuperadmin) return true;
                                const myRole = userAppRoles.find(
                                  (r) =>
                                    r.user_id === user?.uid &&
                                    r.app_id === a.id &&
                                    r.enabled,
                                );
                                return myRole?.role === "admin";
                              })
                              .map((app) => {
                                const selected = editApps[app.id]?.enabled;
                                return (
                                  <div
                                    key={app.id}
                                    style={{
                                      padding: "10px 14px",
                                      borderRadius: 8,
                                      border: `2px solid ${selected ? "#2563eb" : "#e2e8f0"}`,
                                      backgroundColor: selected
                                        ? "#eff6ff"
                                        : "white",
                                    }}
                                  >
                                    <label
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        cursor: "pointer",
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={!!selected}
                                        onChange={() => toggleEditApp(app.id)}
                                        style={{
                                          accentColor: "#2563eb",
                                          width: 16,
                                          height: 16,
                                        }}
                                      />
                                      <span
                                        style={{
                                          fontWeight: 600,
                                          fontSize: 13,
                                        }}
                                      >
                                        {app.name}
                                      </span>
                                    </label>
                                    {selected && (
                                      <select
                                        style={{
                                          marginTop: 6,
                                          padding: "4px 8px",
                                          border: "1px solid #e2e8f0",
                                          borderRadius: 6,
                                          fontSize: 12,
                                          width: "100%",
                                        }}
                                        value={editApps[app.id]?.role || "user"}
                                        onChange={(e) =>
                                          setEditApps((prev) => ({
                                            ...prev,
                                            [app.id]: {
                                              enabled: true,
                                              role: e.target.value,
                                            },
                                          }))
                                        }
                                      >
                                        <option value="user">Usuario</option>
                                        <option value="manager">Manager</option>
                                        <option value="admin">Admin</option>
                                      </select>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                          <p
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#64748b",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              marginBottom: 8,
                            }}
                          >
                            Obras asignadas
                          </p>
                          <div style={{ marginBottom: 12 }}>
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 8,
                                cursor: "pointer",
                              }}
                            >
                              <input
                                type="radio"
                                name={`obras-modo-${u.id}`}
                                checked={editObrasModo === "todas"}
                                onChange={() => setEditObrasModo("todas")}
                              />
                              <span style={{ fontSize: 13 }}>
                                Todas las obras de la empresa
                              </span>
                            </label>
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 8,
                                cursor: "pointer",
                              }}
                            >
                              <input
                                type="radio"
                                name={`obras-modo-${u.id}`}
                                checked={editObrasModo === "seleccion"}
                                onChange={() => setEditObrasModo("seleccion")}
                              />
                              <span style={{ fontSize: 13 }}>
                                Solo obras seleccionadas
                              </span>
                            </label>
                            {editObrasModo === "seleccion" && (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 6,
                                  marginTop: 4,
                                  paddingLeft: 24,
                                }}
                              >
                                {obras
                                  .filter(
                                    (o) =>
                                      o.tenant_id ===
                                        (editTenantCtx || u.tenant_id) &&
                                      o.estado === "activa",
                                  )
                                  .map((o) => {
                                    const checked = editObrasSel.includes(o.id);
                                    return (
                                      <label
                                        key={o.id}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 6,
                                          padding: "4px 10px",
                                          borderRadius: 6,
                                          cursor: "pointer",
                                          border: `1px solid ${checked ? "#2563eb" : "#e2e8f0"}`,
                                          backgroundColor: checked
                                            ? "#eff6ff"
                                            : "white",
                                          fontSize: 12,
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() =>
                                            setEditObrasSel((prev) =>
                                              prev.includes(o.id)
                                                ? prev.filter((x) => x !== o.id)
                                                : [...prev, o.id],
                                            )
                                          }
                                          style={{ accentColor: "#2563eb" }}
                                        />
                                        {o.nombre}
                                      </label>
                                    );
                                  })}
                              </div>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button
                              style={styles.btnPrimary}
                              onClick={() => handleSaveApps(u.id)}
                              disabled={savingApps}
                            >
                              {savingApps ? "Guardando..." : "Guardar cambios"}
                            </button>
                            <button
                              style={styles.btnSecondary}
                              onClick={() => setEditingUser(null)}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          style={{ ...styles.btnSmall, marginTop: 12 }}
                          onClick={() => startEditApps(u.id)}
                        >
                          Editar usuario
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── INVITACIONES ── */}
      {activeTab === "invitations" && (
        <div>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              Invitaciones pendientes ({pendingInvitations.length})
            </h2>
            <button
              style={styles.btnPrimary}
              onClick={() => setShowInviteForm(!showInviteForm)}
            >
              {showInviteForm ? "Cancelar" : "+ Nueva invitación"}
            </button>
          </div>

          {showInviteForm && (
            <div style={styles.formCard}>
              <h3 style={styles.formTitle}>Nueva invitación</h3>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email del usuario *</label>
                  <input
                    style={styles.input}
                    type="email"
                    placeholder="usuario@empresa.com"
                    value={inviteData.email}
                    onChange={(e) =>
                      setInviteData({ ...inviteData, email: e.target.value })
                    }
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Rol en la plataforma</label>
                  <select
                    style={styles.input}
                    value={inviteData.role_platform}
                    onChange={(e) =>
                      setInviteData({
                        ...inviteData,
                        role_platform: e.target.value,
                      })
                    }
                  >
                    <option value="user">Usuario</option>
                    <option value="tenant_admin">Administrador</option>
                    {isSuperadmin && (
                      <option value="superadmin">Superadmin</option>
                    )}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Empresa</label>
                  {isSuperadmin ? (
                    <select
                      style={styles.input}
                      value={inviteData.tenant_id}
                      onChange={(e) =>
                        setInviteData({
                          ...inviteData,
                          tenant_id: e.target.value,
                          apps: {},
                        })
                      }
                    >
                      {tenants.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      style={styles.input}
                      value={
                        tenants.find((t) => t.id === inviteData.tenant_id)
                          ?.name || inviteData.tenant_id
                      }
                      disabled
                    />
                  )}
                </div>
              </div>

              <div style={{ marginTop: 20 }}>
                <label style={styles.label}>Apps habilitadas y roles</label>
                <div style={styles.appsGrid}>
                  {apps
                    .filter((a) => {
                      if (!a.active) return false;
                      if (
                        !tenantApps.some(
                          (ta) =>
                            ta.app_id === a.id &&
                            ta.tenant_id === inviteData.tenant_id &&
                            ta.enabled,
                        )
                      )
                        return false;
                      if (isSuperadmin) return true;
                      const myRole = userAppRoles.find(
                        (r) =>
                          r.user_id === user?.uid &&
                          r.app_id === a.id &&
                          r.enabled,
                      );
                      return myRole?.role === "admin";
                    })
                    .map((app) => {
                      const selected = inviteData.apps[app.id]?.enabled;
                      return (
                        <div
                          key={app.id}
                          style={{
                            ...styles.appToggle,
                            borderColor: selected ? "#2563eb" : "#e2e8f0",
                            backgroundColor: selected ? "#eff6ff" : "white",
                          }}
                        >
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!selected}
                              onChange={() => toggleAppInvite(app.id)}
                              style={{
                                accentColor: "#2563eb",
                                width: 16,
                                height: 16,
                              }}
                            />
                            <span style={{ fontWeight: 600, fontSize: 14 }}>
                              {app.name}
                            </span>
                          </label>
                          {selected && (
                            <select
                              style={{
                                ...styles.input,
                                marginTop: 8,
                                fontSize: 13,
                              }}
                              value={inviteData.apps[app.id]?.role || "user"}
                              onChange={(e) =>
                                setAppRole(app.id, e.target.value)
                              }
                            >
                              <option value="user">Usuario</option>
                              <option value="manager">Manager</option>
                              <option value="admin">Admin</option>
                            </select>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button
                  style={styles.btnPrimary}
                  onClick={handleSendInvitation}
                  disabled={saving}
                >
                  {saving
                    ? "Generando..."
                    : "📋 Crear invitación y copiar link"}
                </button>
                <button
                  style={styles.btnSecondary}
                  onClick={() => setShowInviteForm(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {invitations.length > 0 && (
            <Buscador
              value={qInvitations}
              onChange={setQInvitations}
              placeholder="Buscar invitación por email o empresa..."
              resultados={
                pendingFiltradas.length +
                acceptedFiltradas.length +
                historialFiltradas.length
              }
              total={invitations.length}
            />
          )}

          {pendingFiltradas.length === 0 && !showInviteForm ? (
            <div style={styles.empty}>
              {qInvitations.trim()
                ? "Ninguna invitación pendiente coincide con la búsqueda"
                : "No hay invitaciones pendientes"}
            </div>
          ) : (
            <div style={styles.table}>
              {pendingFiltradas.map((inv) => (
                <div
                  key={inv.id}
                  style={{
                    ...styles.tableRow,
                    gridTemplateColumns: "1fr 1fr auto auto auto",
                  }}
                >
                  <span style={styles.bold}>{inv.email}</span>
                  <span style={{ ...styles.muted, fontSize: 13 }}>
                    {hoursLeft(inv)}
                  </span>
                  <button
                    style={styles.btnSmall}
                    onClick={() => handleCopyLink(inv.token)}
                  >
                    {copiedToken === inv.token ? "✓ Copiado" : "📋 Copiar link"}
                  </button>
                  <button
                    style={{
                      ...styles.btnSmall,
                      backgroundColor: "#f0fdf4",
                      color: "#16a34a",
                    }}
                    onClick={() => handleResend(inv)}
                  >
                    ↻ Reenviar
                  </button>
                  <button
                    style={{
                      ...styles.btnSmall,
                      backgroundColor: "#fef2f2",
                      color: "#dc2626",
                    }}
                    onClick={() => handleCancelInvitation(inv.token)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          {acceptedFiltradas.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ ...styles.sectionTitle, marginBottom: 12 }}>
                Invitaciones aceptadas ({acceptedFiltradas.length})
              </h3>
              <div style={styles.table}>
                {acceptedFiltradas.map((inv) => (
                  <div
                    key={inv.id}
                    style={{
                      ...styles.tableRow,
                      gridTemplateColumns: "1fr 1fr 1fr",
                    }}
                  >
                    <span style={styles.bold}>{inv.email}</span>
                    <span style={styles.muted}>
                      {inv.apps?.map((a) => a.app_id).join(", ")}
                    </span>
                    <span
                      style={{
                        ...styles.statusBadge,
                        backgroundColor: "#f0fdf4",
                        color: "#16a34a",
                      }}
                    >
                      Aceptada
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {historialFiltradas.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ ...styles.sectionTitle, marginBottom: 4 }}>
                Historial ({historialFiltradas.length})
              </h3>
              <p style={{ ...styles.muted, fontSize: 13, marginBottom: 12 }}>
                Invitaciones anuladas o vencidas. Se conservan como registro:
                nada se borra.
              </p>
              <div style={styles.table}>
                {historialFiltradas.map((inv) => {
                  const anulada = inv.status === "cancelled";
                  const etiqueta = anulada
                    ? inv.cancelled_motivo === "reemplazada"
                      ? "Reemplazada"
                      : "Anulada"
                    : "Vencida";
                  return (
                    <div
                      key={inv.id}
                      style={{
                        ...styles.tableRow,
                        gridTemplateColumns: "1.3fr 1.2fr 1.2fr auto",
                      }}
                    >
                      <span style={styles.bold}>{inv.email}</span>
                      <span style={{ ...styles.muted, fontSize: 12 }}>
                        {"Invitada: " +
                          fechaCorta(inv.created_at) +
                          (inv.created_by ? " · por " + inv.created_by : "")}
                      </span>
                      <span style={{ ...styles.muted, fontSize: 12 }}>
                        {anulada
                          ? "Anulada: " +
                            fechaCorta(inv.cancelled_at) +
                            (inv.cancelled_by
                              ? " · por " + inv.cancelled_by
                              : "")
                          : "Venció: " + fechaCorta(inv.expires_at)}
                      </span>
                      <span
                        style={{
                          ...styles.statusBadge,
                          backgroundColor: anulada ? "#fef2f2" : "#f8fafc",
                          color: anulada ? "#dc2626" : "#64748b",
                        }}
                      >
                        {etiqueta}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── APPS ── */}
      {activeTab === "apps" && (
        <div>
          <h2 style={{ ...styles.sectionTitle, marginBottom: 16 }}>
            Aplicaciones disponibles
          </h2>
          <Buscador
            value={qApps}
            onChange={setQApps}
            placeholder="Buscar app por nombre, tipo o URL..."
            resultados={appsFiltradas.length}
            total={apps.length}
          />
          <div style={styles.table}>
            <div
              style={{
                ...styles.tableRow,
                ...styles.tableHeader,
                gridTemplateColumns: "1fr 1fr 2fr auto",
              }}
            >
              <span>App</span>
              <span>Tipo</span>
              <span>URL</span>
              <span>Estado</span>
            </div>
            {appsFiltradas.map((app) => (
              <div
                key={app.id}
                style={{
                  ...styles.tableRow,
                  gridTemplateColumns: "1fr 1fr 2fr auto",
                }}
              >
                <span style={styles.bold}>{app.name}</span>
                <span style={styles.muted}>{app.type}</span>
                <span style={{ ...styles.muted, fontSize: 12 }}>
                  {app.url || "interno"}
                </span>
                <span
                  style={{
                    ...styles.statusBadge,
                    backgroundColor: app.active ? "#f0fdf4" : "#f5f5f5",
                    color: app.active ? "#16a34a" : "#9e9e9e",
                  }}
                >
                  {app.active ? "Activa" : "Inactiva"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "obras" && (
        <div>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Obras ({obras.length})</h2>

            <button
              style={styles.btnPrimary}
              onClick={() => setShowObraForm(!showObraForm)}
            >
              {showObraForm ? "Cancelar" : "+ Nueva obra"}
            </button>
          </div>

          {showObraForm && (
            <div style={styles.formCard}>
              <h3 style={styles.formTitle}>Nueva obra</h3>
              <div style={{ ...styles.formGrid, marginBottom: 12 }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nombre *</label>
                  <input
                    style={styles.input}
                    placeholder="Ej: Barrio San Agustín - Etapa 1"
                    value={obraData.nombre}
                    onChange={(e) =>
                      setObraData({ ...obraData, nombre: e.target.value })
                    }
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Dirección</label>
                  <input
                    style={styles.input}
                    placeholder="Calle, localidad"
                    value={obraData.direccion}
                    onChange={(e) =>
                      setObraData({ ...obraData, direccion: e.target.value })
                    }
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Comitente</label>
                  <input
                    style={styles.input}
                    placeholder="Ej: Municipalidad de Mendoza"
                    value={obraData.comitente}
                    onChange={(e) =>
                      setObraData({ ...obraData, comitente: e.target.value })
                    }
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Tipo de obra</label>
                  <select
                    style={styles.input}
                    value={obraData.tipo_obra}
                    onChange={(e) =>
                      setObraData({ ...obraData, tipo_obra: e.target.value })
                    }
                  >
                    <option value="VIVIENDAS_UNIFAMILIARES">
                      Viviendas Unifamiliares
                    </option>
                    <option value="VIVIENDAS_MULTIFAMILIARES">
                      Viviendas Multifamiliares
                    </option>
                    <option value="EDIFICIO_COMERCIAL">
                      Edificio Comercial
                    </option>
                    <option value="INFRAESTRUCTURA">Infraestructura</option>
                    <option value="INDUSTRIAL">Industrial</option>
                    <option value="SANEAMIENTO">Saneamiento</option>
                    <option value="PAVIMENTACION">Pavimentación</option>
                    <option value="HOTEL">Hotel</option>
                    <option value="GALPON">Galpón</option>
                    <option value="EDIFICIO_OFICINAS">
                      Edificio de Oficinas
                    </option>
                    <option value="BARRIO_PRIVADO">Barrio Privado</option>
                    <option value="AEROPUERTO">Aeropuerto</option>
                    <option value="COMPLEJO_PENITENCIARIO">
                      Complejo Penitenciario
                    </option>
                    <option value="EDIFICIO_USOS_MIXTOS">
                      Edificio de Usos Mixtos
                    </option>
                    <option value="OTRO">Otro</option>
                  </select>
                </div>
              </div>
              {isSuperadmin && (
                <div style={{ ...styles.formGroup, marginBottom: 16 }}>
                  <label style={styles.label}>Empresa *</label>
                  <select
                    style={styles.input}
                    value={obraData.tenant_id}
                    onChange={(e) =>
                      setObraData({ ...obraData, tenant_id: e.target.value })
                    }
                  >
                    <option value="">Seleccioná una empresa</option>
                    {tenants.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  style={styles.btnPrimary}
                  onClick={handleCrearObra}
                  disabled={savingObra}
                >
                  {savingObra ? "Guardando..." : "Crear obra"}
                </button>
                <button
                  style={styles.btnSecondary}
                  onClick={() => setShowObraForm(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {obras.length > 0 && !obrasLoading && (
            <Buscador
              value={qObras}
              onChange={setQObras}
              placeholder="Buscar obra por nombre, comitente, dirección o empresa..."
              resultados={obrasFiltradas.length}
              total={obras.length}
            />
          )}

          {obrasLoading ? (
            <p style={{ color: "#64748b", fontSize: 13 }}>Cargando obras...</p>
          ) : obras.length === 0 ? (
            <div style={styles.empty}>No hay obras cargadas</div>
          ) : obrasFiltradas.length === 0 ? (
            <div style={styles.empty}>
              Ninguna obra coincide con la búsqueda
            </div>
          ) : (
            <div style={styles.table}>
              <div
                style={{
                  ...styles.tableRow,
                  ...styles.tableHeader,
                  gridTemplateColumns: "1fr 1fr 100px 60px",
                }}
              >
                <span>Nombre</span>
                <span>Empresa</span>
                <span style={{ textAlign: "center" }}>Estado</span>
                <span></span>
              </div>
              {obrasFiltradas.map((obra) => {
                const isExp = expandedObra === obra.id;
                const isEditing = editingObra === obra.id;
                return (
                  <div key={obra.id}>
                    <div
                      style={{
                        ...styles.tableRow,
                        gridTemplateColumns: "1fr 1fr 100px 60px",
                        cursor: "pointer",
                        backgroundColor: isExp ? "#f8fafc" : "white",
                      }}
                      onClick={() => setExpandedObra(isExp ? null : obra.id)}
                    >
                      <span style={styles.bold}>
                        {isExp ? "▼" : "▶"} {obra.nombre}
                      </span>
                      <span style={styles.muted}>
                        {tenants.find((t) => t.id === obra.tenant_id)?.name ||
                          obra.tenant_id}
                      </span>
                      <span
                        style={{
                          ...styles.statusBadge,
                          textAlign: "center",
                          backgroundColor:
                            obra.estado === "activa" ? "#f0fdf4" : "#f1f5f9",
                          color:
                            obra.estado === "activa" ? "#16a34a" : "#64748b",
                        }}
                      >
                        {obra.estado === "activa" ? "Activa" : "Cerrada"}
                      </span>
                      <span></span>
                    </div>

                    {isExp && (
                      <div
                        style={{
                          padding: "16px 20px 20px 40px",
                          backgroundColor: "#f8fafc",
                          borderTop: "1px solid #e2e8f0",
                        }}
                      >
                        {isEditing ? (
                          <div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: 12,
                                marginBottom: 12,
                              }}
                            >
                              <div style={styles.formGroup}>
                                <label style={styles.label}>Nombre *</label>
                                <input
                                  style={styles.input}
                                  value={editObraData.nombre}
                                  onChange={(e) =>
                                    setEditObraData({
                                      ...editObraData,
                                      nombre: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div style={styles.formGroup}>
                                <label style={styles.label}>Dirección</label>
                                <input
                                  style={styles.input}
                                  value={editObraData.direccion}
                                  onChange={(e) =>
                                    setEditObraData({
                                      ...editObraData,
                                      direccion: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div style={styles.formGroup}>
                                <label style={styles.label}>Comitente</label>
                                <input
                                  style={styles.input}
                                  value={editObraData.comitente}
                                  onChange={(e) =>
                                    setEditObraData({
                                      ...editObraData,
                                      comitente: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div style={styles.formGroup}>
                                <label style={styles.label}>Tipo de obra</label>
                                <select
                                  style={styles.input}
                                  value={editObraData.tipo_obra}
                                  onChange={(e) =>
                                    setEditObraData({
                                      ...editObraData,
                                      tipo_obra: e.target.value,
                                    })
                                  }
                                >
                                  <option value="VIVIENDAS_UNIFAMILIARES">
                                    Viviendas Unifamiliares
                                  </option>
                                  <option value="VIVIENDAS_MULTIFAMILIARES">
                                    Viviendas Multifamiliares
                                  </option>
                                  <option value="EDIFICIO_COMERCIAL">
                                    Edificio Comercial
                                  </option>
                                  <option value="INFRAESTRUCTURA">
                                    Infraestructura
                                  </option>
                                  <option value="INDUSTRIAL">Industrial</option>
                                  <option value="SANEAMIENTO">
                                    Saneamiento
                                  </option>
                                  <option value="PAVIMENTACION">
                                    Pavimentación
                                  </option>
                                  <option value="HOTEL">Hotel</option>
                                  <option value="GALPON">Galpón</option>
                                  <option value="EDIFICIO_OFICINAS">
                                    Edificio de Oficinas
                                  </option>
                                  <option value="BARRIO_PRIVADO">
                                    Barrio Privado
                                  </option>
                                  <option value="AEROPUERTO">Aeropuerto</option>
                                  <option value="COMPLEJO_PENITENCIARIO">
                                    Complejo Penitenciario
                                  </option>
                                  <option value="EDIFICIO_USOS_MIXTOS">
                                    Edificio de Usos Mixtos
                                  </option>
                                  <option value="OTRO">Otro</option>
                                </select>
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                style={styles.btnPrimary}
                                onClick={handleGuardarEditObra}
                                disabled={savingEditObra}
                              >
                                {savingEditObra
                                  ? "Guardando..."
                                  : "Guardar cambios"}
                              </button>
                              <button
                                style={styles.btnSecondary}
                                onClick={() => setEditingObra(null)}
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: 12,
                                marginBottom: 16,
                              }}
                            >
                              <div>
                                <p
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#64748b",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    marginBottom: 4,
                                    margin: "0 0 4px 0",
                                  }}
                                >
                                  Dirección
                                </p>
                                <p
                                  style={{
                                    fontSize: 13,
                                    color: "#1e293b",
                                    margin: 0,
                                  }}
                                >
                                  {obra.direccion || "—"}
                                </p>
                              </div>
                              <div>
                                <p
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#64748b",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    margin: "0 0 4px 0",
                                  }}
                                >
                                  Comitente
                                </p>
                                <p
                                  style={{
                                    fontSize: 13,
                                    color: "#1e293b",
                                    margin: 0,
                                  }}
                                >
                                  {obra.comitente || "—"}
                                </p>
                              </div>
                              <div>
                                <p
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#64748b",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    margin: "0 0 4px 0",
                                  }}
                                >
                                  Tipo
                                </p>
                                <p
                                  style={{
                                    fontSize: 13,
                                    color: "#1e293b",
                                    margin: 0,
                                  }}
                                >
                                  {obra.tipo_obra?.replace(/_/g, " ") || "—"}
                                </p>
                              </div>
                            </div>

                            <p
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#64748b",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                margin: "0 0 8px 0",
                              }}
                            >
                              Apps habilitadas
                            </p>
                            <div
                              style={{
                                display: "flex",
                                gap: 12,
                                marginBottom: 16,
                                flexWrap: "wrap",
                              }}
                            >
                              {["hys", "cauciones"].map((appId) => {
                                const docId = `${obra.tenant_id}_${appId}`;
                                const config = appObras[docId];
                                const habilitada =
                                  !config ||
                                  config.modo === "todas" ||
                                  (config.obras_habilitadas || []).includes(
                                    obra.id,
                                  );
                                return (
                                  <label
                                    key={appId}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      padding: "8px 14px",
                                      border: `2px solid ${habilitada ? "#16a34a" : "#e2e8f0"}`,
                                      backgroundColor: habilitada
                                        ? "#f0fdf4"
                                        : "white",
                                      borderRadius: 8,
                                      cursor: "pointer",
                                      fontSize: 13,
                                      fontWeight: 600,
                                      color: habilitada ? "#16a34a" : "#94a3b8",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={habilitada}
                                      onChange={() =>
                                        toggleObraEnApp(
                                          obra.id,
                                          obra.tenant_id,
                                          appId,
                                          !habilitada,
                                        )
                                      }
                                      style={{
                                        accentColor: "#16a34a",
                                        width: 14,
                                        height: 14,
                                      }}
                                    />
                                    {appId === "hys"
                                      ? "Higiene y Seguridad"
                                      : "Gestión de Cobertura"}
                                  </label>
                                );
                              })}
                            </div>

                            <div style={{ display: "flex", gap: 8 }}>
                              <button
                                style={styles.btnSmall}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingObra(obra.id);
                                  setEditObraData({
                                    nombre: obra.nombre || "",
                                    direccion: obra.direccion || "",
                                    comitente: obra.comitente || "",
                                    tipo_obra:
                                      obra.tipo_obra ||
                                      "VIVIENDAS_UNIFAMILIARES",
                                  });
                                }}
                              >
                                Editar obra
                              </button>
                              {obra.estado === "activa" ? (
                                <button
                                  style={{
                                    ...styles.btnSmall,
                                    color: "#dc2626",
                                    border: "1px solid #fca5a5",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCambiarEstadoObra(obra.id, "cerrada");
                                  }}
                                >
                                  Cerrar obra
                                </button>
                              ) : (
                                <button
                                  style={{
                                    ...styles.btnSmall,
                                    color: "#16a34a",
                                    border: "1px solid #86efac",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCambiarEstadoObra(obra.id, "activa");
                                  }}
                                >
                                  Reactivar obra
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === "audit" && (
        <AuditoriaPanel
          isSuperadmin={isSuperadmin}
          userProfile={userProfile}
          tenants={tenants}
        />
      )}

      {/* ── INTEGRACIONES (API pública, sólo superadmin) ── */}
      {activeTab === "integraciones" && isSuperadmin && (
        <IntegracionesPanel tenants={tenants} />
      )}
    </div>
  );
}

const styles = {
  container: { padding: "32px", maxWidth: "1000px", margin: "0 auto" },
  header: { marginBottom: 24 },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: "#1e293b",
    margin: "0 0 4px 0",
  },
  subtitle: { fontSize: 14, color: "#64748b", margin: 0 },
  tabs: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 },
  tab: {
    padding: "10px 20px",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    backgroundColor: "white",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    color: "#64748b",
  },
  tabActive: {
    backgroundColor: "#1e293b",
    color: "white",
    borderColor: "#1e293b",
  },
  message: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 9999,
    padding: "14px 20px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    boxShadow: "0 6px 24px rgba(0,0,0,0.18)",
    maxWidth: 360,
    animation: "slideInToast 0.25s ease-out",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  sectionTitle: { fontSize: 18, fontWeight: 600, color: "#1e293b", margin: 0 },
  btnPrimary: {
    padding: "10px 20px",
    backgroundColor: "#1e293b",
    color: "white",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
  },
  btnSecondary: {
    padding: "10px 20px",
    backgroundColor: "transparent",
    color: "#64748b",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 14,
  },
  btnSmall: {
    padding: "6px 12px",
    backgroundColor: "#f8fafc",
    color: "#475569",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
  },
  formCard: {
    backgroundColor: "white",
    padding: 24,
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    marginBottom: 24,
  },
  formTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#1e293b",
    margin: "0 0 16px 0",
  },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 },
  formGroup: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  input: {
    padding: "10px 14px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  appsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 12,
    marginTop: 8,
  },
  appToggle: {
    padding: 14,
    borderRadius: 10,
    border: "2px solid",
    transition: "all 0.15s",
  },
  table: {
    backgroundColor: "white",
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  tableHeader: {
    backgroundColor: "#f8fafc",
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  tableRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 120px 100px",
    padding: "14px 20px",
    borderTop: "1px solid #f1f5f9",
    alignItems: "center",
    gap: 12,
  },
  bold: { fontSize: 14, fontWeight: 600, color: "#1e293b" },
  muted: { fontSize: 14, color: "#64748b" },
  badge: {
    padding: "4px 10px",
    borderRadius: 99,
    fontSize: 12,
    fontWeight: 600,
    backgroundColor: "#f1f5f9",
    color: "#475569",
    display: "inline-block",
  },
  statusBadge: {
    padding: "4px 10px",
    borderRadius: 99,
    fontSize: 12,
    fontWeight: 600,
    display: "inline-block",
  },
  empty: { textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 14 },
};